import { Core } from "@faire/design-tokens";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Span } from "@faire/web--source/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

const VALUE_PROP_ICON_SIZE = 32;
export const ShowroomValueTitleBanner = () => {
  const { layoutElement } = React.useContext(Context);
  const isSSR = useIsSSR();
  const {
    main_text,
    main_text_color,
    tagline_text,
    tagline_text_color,
    layout_element_data,
    background_color,
  } = layoutElement;

  const vettingCriteriaData = layout_element_data
    .map((data) => data.simple_image_title)
    .filter(isNotUndefined);
  const hasVettingCriteria = vettingCriteriaData.length > 0;

  return (
    <Wrapper justify="center" $backgroundColor={background_color}>
      {main_text && tagline_text ? (
        <TitleContainer>
          <Span
            variant="displaySSerifRegular"
            color={main_text_color ? main_text_color : Core.text.inverse}
          >
            {main_text}
          </Span>{" "}
          <Span
            variant="displaySSerifRegular"
            color={tagline_text_color ? tagline_text_color : Core.text.subdued}
          >
            {tagline_text}
          </Span>
        </TitleContainer>
      ) : null}
      {hasVettingCriteria ? (
        <VettingCriteriaWrapper>
          {vettingCriteriaData.map((vettingCriteria) => {
            const { image, main_text, main_text_color } = vettingCriteria;
            if (image === undefined || !main_text) {
              return null;
            }
            return (
              <VettingCriteria key={main_text} align="center">
                <img
                  style={{
                    width: `${VALUE_PROP_ICON_SIZE}px`,
                    height: `${VALUE_PROP_ICON_SIZE}px`,
                  }}
                  src={optimizeImageUrlFillSquare(
                    image,
                    isSSR,
                    VALUE_PROP_ICON_SIZE
                  )}
                />
                <Span color={main_text_color}>{main_text}</Span>
              </VettingCriteria>
            );
          })}
        </VettingCriteriaWrapper>
      ) : null}
    </Wrapper>
  );
};

const horizontalPaddingStyles = css`
  padding-left: ${getSpacing("2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding-left: ${getSpacing("3x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-left: ${getSpacing("8x")};
  }
`;

const Wrapper = styled(Column).attrs({ gap: "2x" })<{
  $backgroundColor: string | undefined;
}>`
  width: 100%;
  overflow: hidden;

  padding: ${getSpacing("3x", "1x")};
  ${({ $backgroundColor }) =>
    $backgroundColor
      ? css`
          background-color: ${$backgroundColor};
        `
      : css`
          background-color: ${Core.surface.enabled};
        `}
`;

const TitleContainer = styled.div`
  ${horizontalPaddingStyles};
`;

const VettingCriteria = styled(Row).attrs({ gap: 8 })`
  white-space: nowrap;
`;

const VettingCriteriaWrapper = styled(Row).attrs({ gap: 32 })`
  overflow: auto;
  ${horizontalPaddingStyles}

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0);
  }
`;
